import { getApps, initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getDatabase,
  connectDatabaseEmulator,
  ref,
  update,
  onDisconnect,
  onValue,
  off,
  push,
  remove,
  serverTimestamp,
  type Database,
  type DataSnapshot
} from 'firebase/database';
import {
  getAuth,
  initializeAuth,
  inMemoryPersistence,
  signInWithCustomToken,
  signOut,
  type Auth
} from 'firebase/auth';
import { postWithAuth } from '@services/http.service';
import { getAuth as getStoredAuth, isRealUser } from '@services/identity.service';
import { RTDB_TOKEN_URL } from '@services/url.service';
import { getOrCreateDeviceId } from './device';
import { getDeviceMeta } from './deviceMeta';
import { commandsPath, mergeBotReaders, mergeExtensionReaders } from './presence';
import { stripUndefined } from './sanitize';

// Web-app side of the Firebase RTDB control plane (see refs/realtime-sync). RTDB carries
// control/state only; everything is scoped to `/sync/{ownerId}` where ownerId is resolved
// server-side by POST /realtime-sync/token and enforced by RTDB security rules.

export interface LivePage {
  url: string;
  platform: string;
  livestreamId: string | null;
  state: 'open' | 'connected';
  // Whether the extension can currently read this open page's chat (reported by
  // the pre-connect readability probe). Present only for un-connected (`open`)
  // pages; once connected, readability rides the session's `chatReadable`.
  // Undefined = not yet reported (treat as not-readable for gating).
  chatReadable?: boolean;
  // How hard Chrome is throttling the tab (refs/reader-ownership.md). Absent on
  // extensions before 6.1 and until the page first reports.
  throttle?: TabThrottle;
  throttleSince?: number;
}

// none: visible · background: hidden, ~1s timers · intensive: hidden 5+ min,
// ~1 min timers · frozen: Energy Saver, nothing runs · discarded: unloaded.
export type TabThrottle = 'none' | 'background' | 'intensive' | 'frozen' | 'discarded';

// What an extension reader says it understands (refs/reader-ownership.md):
// `inbox` = addressed commands on `/readers/{id}/commands`; `lease` = session
// ownership. Absent on extensions before 6.1.
export type ReaderCapability = 'inbox' | 'lease';

// One extension reader (a browser profile with the extension), kept apart from the
// merged view so the app can choose which browser reads a session.
export interface ExtensionReader {
  readerId: string;
  online: boolean;
  version?: string | null;
  userId?: string | null;
  userEmail?: string | null;
  label?: string | null;
  capabilities: ReaderCapability[];
  pages: Record<string, LivePage>;
  lastSeen?: number;
}

export interface ExtensionPresence {
  installed: boolean;
  online: boolean;
  pages: Record<string, LivePage>;
  // Every extension reader, per browser. `pages` above is their union.
  readers: ExtensionReader[];
}

// Both projections of one `/sync/{ownerId}/readers` snapshot, delivered together
// so the app keeps a single listener for the whole collection.
export interface ReadersState {
  presence: ExtensionPresence;
  bots: Record<string, BotPresence>;
}

// Every chat *reader* registers a presence node under `/sync/{ownerId}/readers/{readerId}`
// with a `type`: the browser extension today, meeting bots and server-side API pollers
// next. They share one collection so the app can see all live sources in one place.
// `installed`/`version`/`pages` are extension-specific and absent for other reader types.
export type ReaderType = 'extension' | 'bot' | 'api' | 'stream';

// Platform-neutral bot lifecycle, shared by every bot that publishes a reader:
// the Zoom Meeting SDK bot (in-browser and headless) and the Attendee.dev bots
// behind Meet and Teams. functions/resources/realtime-sync/bot-status.js is the
// source of truth; this is a hand-kept mirror, as is BotStatus in
// apps/zoom-sdk/src/bot/types.ts (each writer ships as its own package, so the
// type cannot be imported). Keep all three in step.
//
// `dispatched` is written by the dispatcher before any bot exists; `admitted`
// means in the meeting room but not yet reading (Attendee wires chat up as a
// separate step). `waiting_room` <-> `in_meeting` is a cycle: a host may park an
// admitted bot back in the waiting room and re-admit it any number of times.
export type BotReaderStatus =
  | 'dispatched'
  | 'joining'
  | 'waiting_room'
  | 'admitted'
  | 'in_meeting'
  | 'kicked'
  | 'ended'
  | 'failed';

// A meeting bot's live state, projected out of the readers snapshot and keyed by
// the livestream it was dispatched for. This is what the app renders bot-status
// notifications from once BotTracker has closed — the Firestore stream doc only
// carries the server-side bot, and only until the session goes live.
export interface BotPresence {
  livestreamId: string;
  status?: BotReaderStatus;
  // status === 'in_meeting'; the only state in which the bot is reading chat.
  isInMeeting: boolean;
  platform?: string;
  runtime?: 'client' | 'server';
  meetingId?: string;
  // The platform's own wording for this state (e.g. Attendee's raw status, or a
  // provider failure message). Diagnostics and optional UI detail only — never
  // branch on it, that is what `status` is for.
  detail?: string;
  lastSeen?: number;
}

export interface ReaderPresence extends Partial<Omit<ExtensionPresence, 'readers'>> {
  type?: ReaderType;
  version?: string | null;
  // Extension readers (6.1+): who the browser belongs to and what it understands.
  userId?: string | null;
  userEmail?: string | null;
  label?: string | null;
  capabilities?: ReaderCapability[];
  lastSeen?: number;
  livestreamId?: string;
  platform?: string;
  runtime?: 'client' | 'server';
  meetingId?: string;
  detail?: string;
  // `type:'bot'` readers only (published by apps/zoom-sdk/src/services/botReader.ts).
  // The bot's lifecycle state, and the value every bot-status notification is
  // rendered from. apps/zoom-sdk/src/bot/types.ts owns the vocabulary — keep the
  // two in step.
  status?: BotReaderStatus;
  // Derived from `status` on write: true only for 'in_meeting'. Anything picking a
  // live reader must use this, never mere connectedness.
  isInMeeting?: boolean;
}

// Where a presentation that drives reading is running. Control-plane only.
export type RuntimeEnv = 'app' | 'embed' | 'powerpoint';

// --- Phase 2: sessions + command channel -----------------------------------

// `pending` — the extension accepted a `startReading` but resolved no tab to bind
// to yet (its live page isn't open, or several tabs matched). The row exists so
// the banner can say why, and flips to `reading` the moment a tab binds. It is
// deliberately NOT `reading`: anything picking a reader for a platform counts
// `reading` rows, and unbound connects published as `reading` made every stale
// attempt an indistinguishable candidate.
export type ReadingStatus = 'reading' | 'pending' | 'stopped';

// Per-livestream session state, published by the extension under
// `/sync/{ownerId}/sessions/{livestreamId}` in response to commands.
export interface SessionState {
  livestreamId: string;
  status: ReadingStatus;
  platform?: string;
  readerId?: string; // reader (extension device / bot / poller) currently handling the session
  readerType?: ReaderType;
  // Where the presentation that triggered reading runs (set from the command).
  runtimeEnv?: RuntimeEnv;
  // For iframe embeds, a label for the embedding platform: the product name when
  // recognised (e.g. 'Gamma'), otherwise the bare host domain — so the extension
  // popup can name where the presentation is running.
  embedHost?: string;
  // The interaction currently live in the session — id only, never content.
  activeInteractionId?: string;
  // The live-page URL the extension recorded at startReading (used to match a
  // session to its live tab, e.g. when overriding the reader for the same page).
  url?: string;
  startedAt?: number;
  updatedAt?: number;
  // Whether the extension can currently see the platform's chat container. When
  // false, chat is not being read (e.g. the chat panel is closed or the page
  // layout changed) and the app/embed should warn the user. Undefined = unknown.
  chatReadable?: boolean;
  // Set when a `startReading` command couldn't resolve a tab to bind to, so the
  // app/embed can show actionable copy instead of a silent hang. Cleared (null)
  // the moment a tab connects. `expectedUrl` is the command's url, for `noTabFound`.
  reason?: 'noTabFound' | 'multipleTabs' | null;
  expectedUrl?: string | null;
  // The ownership lease (refs/reader-ownership.md): `readerId` is its holder.
  leaseEpoch?: number;
  claimedAt?: number;
  claimedBy?: 'command' | 'takeover' | 'resume';
  leaseBound?: boolean;
  // The bound tab's throttle state, written by the holder.
  throttle?: TabThrottle;
  throttleSince?: number;
}

export type CommandType = 'startReading' | 'stopReading';

// A command the app/embed pushes onto `/sync/{ownerId}/commands` for the
// extension to act on. `url`/`platform` help the extension locate the live tab
// when the caller (e.g. an embed) only knows the livestream, not the tab.
export interface SyncCommandInput {
  type: CommandType;
  livestreamId: string;
  url?: string;
  platform?: string;
  // Optional hints the reader mirrors onto the session it publishes.
  runtimeEnv?: RuntimeEnv;
  embedHost?: string;
  activeInteractionId?: string;
  // Task B1: caller-supplied correlation id for the ack trail at
  // `/sync/{ownerId}/acks/{commandId}`. Generated by `sendCommand` when
  // omitted — set explicitly when the caller wants to `subscribeToAck` on it
  // before the write resolves (avoids a race against the extension's first hop).
  commandId?: string;
  // Address the command to one extension's inbox instead of the shared queue
  // (refs/reader-ownership.md). Only for a reader with the `inbox` capability.
  targetReaderId?: string;
  // The presenter chose this reader explicitly: it may take the session from
  // another reader that holds it.
  takeover?: boolean;
}

// --- Task B1: command acknowledgements --------------------------------------
//
// Per-hop trail the extension writes while acting on a command (see
// extension/js/background/realtime-sync.js handleSyncCommand): received →
// tabMatched (or noTabFound/multipleTabs) → overlayDelivered (or
// overlayFailed) → ok/failed. Lets a caller await a real outcome with a
// timeout instead of only inferring one from `/sessions` (which the extension
// may never touch, e.g. if it never signed in at all).
// `leasedElsewhere` is final: another extension holds the session and is reading
// it, so this one refused the command and did nothing (refs/reader-ownership.md).
export type AckHop =
  | 'received' | 'tabMatched' | 'noTabFound' | 'multipleTabs'
  | 'overlayDelivered' | 'overlayFailed' | 'ok' | 'failed' | 'leasedElsewhere';

// Why a terminal `failed` hop failed. `failed` overwrites the hop before it, so
// the extension carries that cause here: `noTabFound` / `multipleTabs` /
// `overlayFailed`, or `lease:<reason>` for a refused claim. Absent from
// extensions older than this field.
export type AckFailureReason = 'noTabFound' | 'multipleTabs' | 'overlayFailed' | `lease:${string}`;

export interface AckState {
  hop: AckHop;
  reason?: AckFailureReason | null;
  lastError?: string | null;
  holderId?: string | null;
  updatedAt?: number;
}

// --- Isolated Firebase app for the RTDB control plane ----------------------
//
// The control plane signs in as the *team owner* via a custom token. That
// identity must never touch the app's primary Firebase auth — the signed-in
// user that Firestore reads/writes depend on. Sharing the default `auth` here
// silently swaps the user out from under Firestore (reads then fail with
// permission-denied and the Listen channel closes). So we run on a dedicated
// secondary app with its own in-memory auth and RTDB handle, exactly like the
// SharedWorker (rtdb.worker.ts).
const RTDB_APP_NAME = 'sa-rtdb-sync';
const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_BUCKET,
  databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL
};
const rtdbApp: FirebaseApp =
  getApps().find((a) => a.name === RTDB_APP_NAME) ??
  initializeApp(firebaseConfig, RTDB_APP_NAME);
// initializeAuth throws if the app already has an auth instance (e.g. HMR
// re-evaluates this module); fall back to the existing one.
let rtdbAuth: Auth;
try {
  rtdbAuth = initializeAuth(rtdbApp, { persistence: inMemoryPersistence });
} catch {
  rtdbAuth = getAuth(rtdbApp);
}
// Exported so any control-plane feature that already resolves its own owner can
// write through the same isolated app/socket instead of opening a second one.
export const rtdbDb: Database = getDatabase(rtdbApp);

// Point this RTDB instance at the local emulator when configured (dev only), so
// the embed reads the same control plane the extension writes to. Auth stays on
// cloud — the emulator accepts the cloud-minted custom token. Wrapped in try/catch
// because connectDatabaseEmulator throws if HMR re-evaluates this module after the
// instance is already in use.
const rtdbEmulatorHost = process.env.NEXT_PUBLIC_FIREBASE_DATABASE_EMULATOR_HOST;
if (rtdbEmulatorHost) {
  const [host, port] = rtdbEmulatorHost.split(':');
  try {
    connectDatabaseEmulator(rtdbDb, host, Number(port) || 9000);
  } catch {
    // already connected (HMR) — ignore
  }
}

// Only a real signed-in user (copilots included, guests excluded) may initiate
// the RTDB sign-in. TeamsGate mounts the realtime-sync transport around the whole
// app — login page included — and the SharedWorker re-asks each frame for a token
// every few seconds until one is supplied. Without this gate, a guest / logged-out
// session answers each ask with POST /realtime-sync/token, which fails ("No Token
// provided") and loops forever. Bailing here (no HTTP call) stops that at the source.
const canFetchRtdbToken = (): boolean => isRealUser(getStoredAuth());

let cachedOwnerId: string | null = null;
let signInPromise: Promise<string | null> | null = null;
// Bumped whenever the expected owner changes, so an in-flight sign-in for the
// previous owner can't repopulate the cache after it was invalidated.
let authEpoch = 0;
let expectedOwnerId: string | null = null;

// Announce which owner this realm should be signed in as (the teams store's
// owner key, set by the transport layer on start). When it changes while we're
// cached as a *different* owner — a team switch — tear the sign-in down so the
// next operation re-fetches a token with the current auth headers and lands
// under the new `/sync/{ownerId}`. Invalidation happens only here, once per
// change (never inside ensureSignedIn), so an owner key whose format never
// string-matches the token's ownerId costs one redundant re-auth, not a loop.
export const setExpectedOwner = (ownerKey: string | null): void => {
  const next = ownerKey ? String(ownerKey) : null;
  if (next === expectedOwnerId) return;
  expectedOwnerId = next;
  if (next && cachedOwnerId && cachedOwnerId !== next) {
    authEpoch += 1;
    cachedOwnerId = null;
    signOut(rtdbAuth).catch(() => {});
  }
};

// Fetch a custom token (scoped to the team owner) and sign the SDK in once.
// Returns the resolved ownerId, or null when not authenticated.
export const ensureSignedIn = async (): Promise<string | null> => {
  if (cachedOwnerId) return cachedOwnerId;
  if (!canFetchRtdbToken()) return null;
  if (signInPromise) return await signInPromise;

  signInPromise = (async () => {
    const epoch = authEpoch;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const res: any = await postWithAuth(RTDB_TOKEN_URL(), {});
      const token = res?.entity?.token;
      const ownerId = res?.entity?.ownerId;
      if (!token || !ownerId) return null;
      await signInWithCustomToken(rtdbAuth, token);
      if (epoch !== authEpoch) {
        // Owner switched while this sign-in was in flight — we just signed in as
        // the stale owner. Undo it; the caller's next operation re-auths fresh.
        signOut(rtdbAuth).catch(() => {});
        return null;
      }
      cachedOwnerId = ownerId;
      return ownerId;
    } catch {
      return null;
    }
  })();

  try {
    return await signInPromise;
  } finally {
    signInPromise = null;
  }
};

// Authenticated RTDB handle for features outside the control plane (Screenshot-Popup,
// FantasticFans, RunOfShow slide-status) whose paths (`screenshots/*`, `slides/*`) now
// require the signed-in rtdb-sync identity. They ride this secondary app instead of the
// default-app `realTimeDb` because the default app must never sign in (see header note).
// Resolves null for guests / logged-out sessions — callers should skip their subscription.
export const getAuthedRtdb = async (): Promise<Database | null> => {
  const ownerId = await ensureSignedIn();
  return ownerId ? rtdbDb : null;
};

// Fetch a custom token + ownerId without signing this realm in. Used to hand
// credentials to the SharedWorker (rtdb.worker.ts), which holds the connection
// and signs itself in; the main thread never opens its own socket in that path.
export const fetchRtdbToken = async (): Promise<{ token: string; ownerId: string } | null> => {
  if (!canFetchRtdbToken()) return null;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res: any = await postWithAuth(RTDB_TOKEN_URL(), {});
    const token = res?.entity?.token;
    const ownerId = res?.entity?.ownerId;
    if (!token || !ownerId) return null;
    return { token, ownerId };
  } catch {
    return null;
  }
};

// Publish this app instance's presence under `/sync/{ownerId}/app/{userId}/{deviceId}`.
export const publishAppPresence = async (
  userId: string,
  deviceId: string,
  context: string = 'web'
): Promise<void> => {
  const ownerId = await ensureSignedIn();
  if (!ownerId || !userId) return;

  const presenceRef = ref(rtdbDb, `sync/${ownerId}/app/${userId}/${deviceId}`);
  await onDisconnect(presenceRef).update({ online: false, lastSeen: serverTimestamp() });
  await update(presenceRef, {
    online: true, context, ...getDeviceMeta(), lastSeen: serverTimestamp()
  });
};

// Bot presence (the `type:'bot'` reader model) is not written from the app at all: the
// bot runs on its own origin and owns that path there, including its own custom-token
// sign-in for headless runtimes (apps/zoom-sdk/src/services/{botReader,rtdb}.ts). A
// future server-dispatched bot (Attendee.dev) follows the same split.

// Subscribe to the readers node for the current owner, projected into the two
// views the app consumes. ONE listener, because both come out of the same
// snapshot: extension readers are merged (installed/online true if any reports
// it, pages unioned), bot readers are keyed by livestream. Returns an
// unsubscribe function.
export const subscribeReaders = (
  onChange: (state: ReadersState) => void
): (() => void) => {
  let detach: (() => void) | null = null;
  let cancelled = false;

  ensureSignedIn().then((ownerId) => {
    if (!ownerId || cancelled) return;
    const readersRef = ref(rtdbDb, `sync/${ownerId}/readers`);
    const handler = (snap: DataSnapshot) => {
      const readers = (snap.val() || {}) as Record<string, ReaderPresence>;
      onChange({
        presence: mergeExtensionReaders(readers),
        bots: mergeBotReaders(readers)
      });
    };
    onValue(readersRef, handler);
    detach = () => off(readersRef, 'value', handler);
  });

  return () => {
    cancelled = true;
    if (detach) detach();
  };
};

// Subscribe to live session state for the current owner. Returns an unsubscribe
// function. Emits `{}` until the SDK is signed in / the node exists.
export const subscribeSessions = (
  onChange: (sessions: Record<string, SessionState>) => void
): (() => void) => {
  let detach: (() => void) | null = null;
  let cancelled = false;

  ensureSignedIn().then((ownerId) => {
    if (!ownerId || cancelled) return;
    const sessionsRef = ref(rtdbDb, `sync/${ownerId}/sessions`);
    const handler = (snap: DataSnapshot) => {
      onChange((snap.val() || {}) as Record<string, SessionState>);
    };
    onValue(sessionsRef, handler);
    detach = () => off(sessionsRef, 'value', handler);
  });

  return () => {
    cancelled = true;
    if (detach) detach();
  };
};

export const generateCommandId = (): string => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `cmd-${Date.now()}-${Math.random().toString(36).slice(2)}`
);

// Push a command onto the owner's command queue. The extension consumes new
// commands from `/sync/{ownerId}/commands`, acts on them, then removes them.
// Resolves with the command's `commandId` once written (not when the
// extension has acted) — pass one in `input.commandId` to `subscribeToAck` on
// it before this resolves (Task B1); otherwise one is generated so the row is
// always ack-able, even if this particular caller doesn't watch it.
export const sendCommand = async (input: SyncCommandInput): Promise<string | null> => {
  const ownerId = await ensureSignedIn();
  if (!ownerId || !input?.livestreamId) return null;
  const commandId = input.commandId || generateCommandId();
  const commandsRef = ref(rtdbDb, commandsPath(ownerId, input));
  // RTDB rejects any write containing `undefined` (it throws synchronously), so
  // drop unset optional fields (e.g. embedHost for non-embed runtimes) first.
  await push(commandsRef, stripUndefined({
    ...input,
    commandId,
    issuedBy: getOrCreateDeviceId(),
    issuedAt: serverTimestamp()
  }));
  return commandId;
};

// Remove a livestream's session row outright.
//
// The extension owns `/sessions` in the happy path (it publishes on start and
// removes on stop), but the app must be able to clear a row itself: a
// `stopReading` the extension never consumes leaves a `reading` row behind, and
// that row alone is enough to resurrect the session — the worker's re-link pass
// matches any open tab against `session.url` and reopens the overlay, which
// auto-connects and re-activates the livestream server-side. Disconnect
// therefore clears the row directly rather than trusting the command to land.
//
// Direct (not via the leader/SharedWorker transport) for the same reason as
// `subscribeToAck` above: a one-off write, not a persistent subscription, so it
// doesn't reintroduce the per-frame socket the transport layer exists to avoid.
export const clearSession = async (livestreamId: string): Promise<void> => {
  const ownerId = await ensureSignedIn();
  if (!ownerId || !livestreamId) return;
  await remove(ref(rtdbDb, `sync/${ownerId}/sessions/${livestreamId}`));
};

// Subscribe to a single command's ack row at `/sync/{ownerId}/acks/{commandId}`.
// Emits `null` until the extension writes the first hop. Returns an
// unsubscribe function.
//
// Deliberately bypasses the leader/SharedWorker transport (unlike presence and
// sessions) and signs into this realm's own isolated RTDB app directly: an ack
// subscription is a single short-lived listener, torn down once the caller
// gets a terminal hop or its own timeout fires — not a persistent per-slide
// subscription — so it doesn't reintroduce the "one socket per embed slide"
// problem the transport layer exists to avoid.
export const subscribeToAck = (
  commandId: string,
  onChange: (ack: AckState | null) => void
): (() => void) => {
  let detach: (() => void) | null = null;
  let cancelled = false;

  ensureSignedIn().then((ownerId) => {
    if (!ownerId || cancelled || !commandId) return;
    const ackRef = ref(rtdbDb, `sync/${ownerId}/acks/${commandId}`);
    const handler = (snap: DataSnapshot) => onChange((snap.val() as AckState) ?? null);
    onValue(ackRef, handler);
    detach = () => off(ackRef, 'value', handler);
  });

  return () => {
    cancelled = true;
    if (detach) detach();
  };
};
